export interface WordpressServiceItem {
  id: number;
  slug?: string;
  link?: string;
  title?: {
    rendered?: string;
  };
  content?: {
    rendered?: string;
    protected?: boolean;
  };
  featured_media?: number;
  acf?: Record<string, any>;
  _embedded?: {
    'wp:featuredmedia'?: Array<{
      source_url?: string;
      media_details?: Record<string, any>;
      alt_text?: string;
    }>;
  };
}

export interface ServiceCard {
  id: number;
  title: string;
  description?: string;
  link?: string;
  imageUrl?: string;
  raw: WordpressServiceItem;
}

export interface WordpressClientItem {
  id: number;
  title?: {
    rendered?: string;
  };
  acf?: {
    // Real ACF field name (confirmed against the live WordPress field group registration
    // and a real wp-admin-created post) is "client_logo" — no "_1" suffix. The previous
    // "client_logo_1" here never matched anything the API actually returns.
    client_logo?: WordpressClientLogo | string | number | null;
  };
  content?: {
    rendered?: string;
  };
}

export interface WordpressClientLogo {
  url?: string;
  source_url?: string;
  alt?: string;
  alt_text?: string;
}

export interface ClientLogoCard {
  id: number;
  title: string;
  imageUrl: string;
  alt: string;
  link?: string;
}

export interface WordpressBlinkProductionItem {
  id: number;
  slug?: string;
  title?: {
    rendered?: string;
  };
  content?: {
    rendered?: string;
  };
  acf?: {
    // Confirmed field names ("Show in REST API" enabled for this field group).
    production_item_image?: string | number | null;
    production_item_title?: string;
    production_item_description?: string;
  };
}

export interface BlinkProductionCard {
  id: number;
  title: string;
  description?: string;
  imageUrl?: string;
  imageAlt: string;
}

export interface WordpressWhyBlinkItem {
  id: number;
  slug?: string;
  title?: {
    rendered?: string;
  };
  acf?: {
    clear_local_coordination_icon?: string | number | null;
    clear_local_coordination?: string;
    clear_local_coordination_description?: string;
  };
}

export interface WhyBlinkCard {
  id: number;
  title: string;
  description?: string;
  iconUrl?: string;
}

export interface WordpressFixerServiceItem {
  id: number;
  slug?: string;
  title?: {
    rendered?: string;
  };
  content?: {
    rendered?: string;
  };
  acf?: {
    service_title?: string;
    description?: string;
    icon?: string | number | null;
    icon_class?: string;
    service_icon?: string;
  };
}

export interface FixerServiceCard {
  id: number;
  title: string;
  description?: string;
  iconUrl?: string;
  iconClass?: string;
  slug?: string;
}

export interface WordpressSceneItem {
  id: number;
  slug?: string;
  title?: {
    rendered?: string;
  };
  content?: {
    rendered?: string;
  };
  featured_media?: number;
  acf?: {
    // Confirmed field names now that "Show in REST API" is enabled for this field group.
    oman_scene_heading?: string;
    oman_scene_details?: string;
    oman_scene_visual?: string | number | null;
    // Earlier speculative guesses kept as fallbacks in case a future item ever
    // uses a differently-configured field group.
    image?: string | number | null;
    scene_image?: string | number | null;
    photo?: string | number | null;
    caption?: string;
    tagline?: string;
    subtitle?: string;
    description?: string;
  };
  _embedded?: {
    'wp:featuredmedia'?: Array<{
      source_url?: string;
      alt_text?: string;
    }>;
  };
}

export interface SceneCard {
  id: number;
  title: string;
  caption?: string;
  imageUrl?: string;
  imageAlt: string;
}

export interface WordpressShowreelHighlightItem {
  id: number;
  slug?: string;
  title?: {
    rendered?: string;
  };
  content?: {
    rendered?: string;
  };
  featured_media?: number;
  acf?: {
    // Confirmed field names ("Show in REST API" enabled for this field group).
    showreel_commercial_heading?: string;
    showreel_commercial_visual?: string | number | null;
    showreel_commercial_video?: string;
  };
  _embedded?: {
    'wp:featuredmedia'?: Array<{
      source_url?: string;
      alt_text?: string;
    }>;
  };
}

export interface ShowreelHighlightCard {
  id: number;
  title: string;
  imageUrl?: string;
  imageAlt: string;
  vimeoUrl?: string;
  embedUrl?: string;
}

export interface WordpressOnSetMomentItem {
  id: number;
  slug?: string;
  title?: {
    rendered?: string;
  };
  content?: {
    rendered?: string;
  };
  featured_media?: number;
  acf?: {
    // Confirmed field names ("Show in REST API" enabled for this field group).
    moment_image?: string | number | null;
    moment_description?: string;
    person_role?: string;
  };
  _embedded?: {
    'wp:featuredmedia'?: Array<{
      source_url?: string;
      alt_text?: string;
    }>;
  };
}

export interface OnSetMomentCard {
  id: number;
  imageUrl?: string;
  imageAlt: string;
  quote?: string;
  personRole: string;
}

export interface WordpressBackdropItem {
  id: number;
  title?: {
    rendered?: string;
  };
  acf?: {
    // Confirmed field names ("Show in REST API" enabled for this field group).
    backdrop_title?: string;
    backdrop_description?: string;
  };
}

export interface BackdropCard {
  id: number;
  title: string;
  description?: string;
}

export interface WordpressAboutOmanItem {
  id: number;
  title?: {
    rendered?: string;
  };
  acf?: {
    // Confirmed field names against the live API response.
    about_oman_title?: string;
    about_oman_description?: string;
  };
}

export interface AboutOmanCard {
  id: number;
  title: string;
  description?: string;
}

export interface WordpressWhatWeHandleItem {
  id: number;
  slug?: string;
  title?: {
    rendered?: string;
  };
  content?: {
    rendered?: string;
  };
  // Confirmed field name now that the ACF field group's location rule ("Post Type is
  // equal to What We Handle") is attached and "Show in REST API" is enabled: acf.handle_item
  // holds the item text. acf was previously an empty array ([]) before the location rule
  // was configured; kept as a loosely-typed fallback shape for that transitional state.
  acf?: { handle_item?: string } | Record<string, any> | any[];
}

export interface WhatWeHandleCard {
  id: number;
  text: string;
}

export interface WordpressGetInTouchItem {
  id: number;
  acf?: {
    // Confirmed field names ("Show in REST API" enabled for this field group).
    contact_form_title?: string;
    contact_name_placeholder?: string;
    contact_email_placeholder?: string;
    contact_phone_placeholder?: string;
    contact_subject_placeholder?: string;
    contact_message_placeholder?: string;
    contact_submit_button_text?: string;
  };
}

export interface GetInTouchContent {
  id: number;
  title: string;
  namePlaceholder: string;
  emailPlaceholder: string;
  phonePlaceholder: string;
  subjectPlaceholder: string;
  messagePlaceholder: string;
  submitButtonText: string;
}

// Mirrors the existing static markup exactly, so the section renders identically
// before the API responds and if the request ever fails.
export const DEFAULT_GET_IN_TOUCH: GetInTouchContent = {
  id: 0,
  title: 'Get In Touch',
  namePlaceholder: 'Name',
  emailPlaceholder: 'Email',
  phonePlaceholder: 'Phone',
  subjectPlaceholder: 'Subject',
  messagePlaceholder: 'Message',
  submitButtonText: 'Send Message'
};
